/**
 * The in-memory response cache.
 *
 * docs/03 §3: "**Cache column** is what the client honours *and* what the
 * origin sets." So the policy is not a hand-maintained table in the client that
 * would drift from the origin — it is parsed from the response's own
 * `Cache-Control`, with the `ETag` stored alongside it. A `304` on the warm
 * pooled connection costs about 90 bytes and one half-RTT, which is why several
 * `no-store` entries are still cheap to re-read after a reconnect.
 *
 * **In-memory, per tab, never `localStorage`** — anything `private` is
 * `no-store` at the origin, and a settled market's `immutable` entry is worth
 * nothing after the tab closes anyway.
 *
 * The one entry that carries the whole caching story: a `SETTLED` market never
 * changes again and is served `max-age=31536000, immutable`. At 2,880 markets a
 * day that is the difference between a History screen that is free after the
 * first visit and one that churns (D4).
 */

export interface CachePolicy {
  store: boolean
  /** Milliseconds of freshness. `0` means "revalidate every time". */
  maxAgeMs: number
  /** Never revalidate. Only ever set on terminal, write-once resources. */
  immutable: boolean
}

export interface CacheEntry<T = unknown> {
  body: T
  etag: string | null
  policy: CachePolicy
  /** Monotonic reading at which the entry was stored or last revalidated. */
  storedAtMonoMs: number
  /** The payload's own `server_time_ms`, kept so a stale read can say how stale. */
  serverTimeMs: number | null
}

export const NO_STORE: CachePolicy = { store: false, maxAgeMs: 0, immutable: false }

/**
 * Parse `Cache-Control`. Absent means do not store: a response that says
 * nothing about its own freshness is not one to guess about.
 */
export function policyFrom(cacheControl: string | null): CachePolicy {
  if (!cacheControl) return NO_STORE
  const directives = cacheControl.toLowerCase()
  if (directives.includes('no-store')) return NO_STORE

  const maxAge = /(?:^|[\s,])max-age=(\d+)/.exec(directives)
  const immutable = directives.includes('immutable')
  const seconds = maxAge ? Number(maxAge[1]) : 0
  const store = immutable || seconds > 0 || directives.includes('no-cache')
  return { store, maxAgeMs: seconds * 1000, immutable }
}

export class ResponseCache {
  readonly #entries = new Map<string, CacheEntry>()
  readonly #monoNow: () => number
  readonly #max: number

  constructor(options: { monoNow: () => number; max?: number }) {
    this.#monoNow = options.monoNow
    this.#max = options.max ?? 500
  }

  get size(): number {
    return this.#entries.size
  }

  get<T>(key: string): CacheEntry<T> | undefined {
    return this.#entries.get(key) as CacheEntry<T> | undefined
  }

  /** Fresh enough to serve without touching the network. */
  isFresh(entry: CacheEntry): boolean {
    if (entry.policy.immutable) return true
    return this.#monoNow() - entry.storedAtMonoMs < entry.policy.maxAgeMs
  }

  set<T>(key: string, entry: Omit<CacheEntry<T>, 'storedAtMonoMs'>): void {
    if (!entry.policy.store && !entry.etag) return
    this.#entries.delete(key)
    this.#entries.set(key, { ...entry, storedAtMonoMs: this.#monoNow() } as CacheEntry)
    while (this.#entries.size > this.#max) {
      const oldest = this.#entries.keys().next()
      if (oldest.done) break
      this.#entries.delete(oldest.value)
    }
  }

  /** A `304`: the body we hold is current again. */
  refresh(key: string, serverTimeMs: number | null): void {
    const entry = this.#entries.get(key)
    if (!entry) return
    entry.storedAtMonoMs = this.#monoNow()
    if (serverTimeMs !== null) entry.serverTimeMs = serverTimeMs
  }

  delete(key: string): void {
    this.#entries.delete(key)
  }

  /** Drop everything for a path prefix — used when a session ends and every
   *  authenticated read in the tab stops being ours to serve. */
  invalidatePrefix(prefix: string): void {
    for (const key of [...this.#entries.keys()]) {
      if (key.includes(` ${prefix}`)) this.#entries.delete(key)
    }
  }

  clear(): void {
    this.#entries.clear()
  }
}
